'use client'

import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/css-utils'

import { useSalaryFilters } from '@/modules/salary/filters/use-salary-filters'
import HistogramChart from '@/modules/salary/histogram/histogram-chart'
import { formatDollar } from '@/utils/currency-formatters'
import { CircleDollarSign, Clock, Palmtree } from 'lucide-react'
import { Fragment } from 'react'
import {
  SalaryRestrictionType,
  useSalaryTableStore,
} from '@/modules/salary/salary-table-context'
import { LockAlertContent } from '@/modules/salary/details/total-comp-alert'
import { HistogramLastUpdatedDate } from './histogram-last-updated-date'
import { useQuery } from '@apollo/client'
import { GET_USER_PROFILE } from '@/modules/user/profile/queries'
import { OnboardingAccountType } from '@/modules/surveys/configs/onboarding/constants'
import { getSalarySeoH1 } from '@/modules/salary/salary-seo-utils'
import { getTotalCompAvg, isHourly } from '../common/utils'
import { useSearchParams } from 'next/navigation'
import { SalaryFilterParam } from '../constants'
import { SalaryPayPeriodToggle } from './salary-pay-period-toggle'
import { getFormattedSalaryCount } from '../common/get-formatted-salary-count'

import type { SalaryHistogramEntry } from '@/modules/salary/types'
import type { SalaryPayPeriod } from '../table/constants'
import type {
  Query,
  SalaryHistogramResponse,
} from '@/lib/generated/graphql-types'

type AveragesHistogramProps = {
  isAuthenticated: boolean
  histogramData?: SalaryHistogramResponse
}

export function AveragesHistogram({
  isAuthenticated,
  histogramData,
}: AveragesHistogramProps) {
  const { salaryFilterControls } = useSalaryFilters({
    isAuthenticated,
  })

  const searchParams = useSearchParams()
  const payPeriod = searchParams?.get(SalaryFilterParam.payPeriod)
  const isHourlyPayPeriod = isHourly(
    salaryFilterControls?.jobType,
    payPeriod as SalaryPayPeriod | null,
  )
  const salaryRestriction = useSalaryTableStore(
    (state) => state.salaryRestriction,
  )

  const { data: userProfileData } = useQuery<Pick<Query, 'getUserProfile'>>(
    GET_USER_PROFILE,
    {
      skip: !isAuthenticated,
    },
  )

  const isStudentOrEmployer =
    userProfileData?.getUserProfile?.onboardingType &&
    [OnboardingAccountType.Student, OnboardingAccountType.Employer].includes(
      userProfileData.getUserProfile.onboardingType as OnboardingAccountType,
    )

  // Student (active or expired) and mismatched profession should see histogram
  const hasBlur =
    (salaryRestriction &&
      !isStudentOrEmployer &&
      salaryRestriction !== SalaryRestrictionType.MismatchedProfession) ??
    false

  const {
    baseSalary: _baseSalary,
    otherIncome: _otherIncome,
    otherIncomePct,
    bonuses: _bonuses,
    bonusesPct,
    paidTimeOffWeeks,
    workloadHoursPerWeek,
    salaryCount,
    histogram,
    histogramTitle,
    updatedAt: histogramUpdatedAt,
  } = histogramData || {}
  const baseSalary = _baseSalary ?? 0
  const otherIncome = _otherIncome ?? 0
  const bonuses = _bonuses ?? 0

  const averageData = [
    {
      label: 'Base',
      Icon: CircleDollarSign,
      value: isHourlyPayPeriod
        ? `${formatDollar(baseSalary)}/hr`
        : formatDollar(baseSalary),
      testId: 'salary-base',
      subItems: [
        {
          label: 'Bonuses',
          value: `${formatDollar(bonuses)} (${bonusesPct}% received bonuses)`,
          testId: 'salary-bonuses',
        },
        {
          label: 'Other income',
          value: `${formatDollar(otherIncome)} (${otherIncomePct}% received other income)`,
          testId: 'salary-other-income',
        },
      ],
    },
    {
      label: 'Workload',
      Icon: Clock,
      value: `~${workloadHoursPerWeek} hrs/week`,
      testId: 'salary-workload',
    },
    {
      label: 'PTO',
      Icon: Palmtree,
      value: `~${paidTimeOffWeeks} weeks`,
      testId: 'salary-pto',
    },
  ]

  const totalCompAvg = getTotalCompAvg(histogramData)
  const formattedSalaryCount = getFormattedSalaryCount(salaryCount)

  const hasHistogramData = Boolean(histogram?.length)
  const isLowData =
    hasHistogramData &&
    (histogram as SalaryHistogramEntry[]).every(
      (item) => item.count === histogram[0].count,
    )

  const locationLabel = salaryFilterControls?.state?.label as string | undefined

  const pageTitle = getSalarySeoH1({
    occupation: histogramTitle,
    location: locationLabel,
    jobType: salaryFilterControls?.jobType,
  })

  return (
    <div className="w-full">
      <Card className="rounded-none border-x-0 w-full bg-background-primary pt-4 md:rounded-xl md:border">
        <CardContent className="grid lg:flex gap-6">
          <div
            className={cn('flex flex-col lg:flex-1', {
              'h-80': hasHistogramData,
            })}
          >
            <div>
              <div className="flex items-center gap-2 justify-between">
                {pageTitle && (
                  <h1 className="text-lg font-medium">{pageTitle}</h1>
                )}
                <span className="hidden text-xs text-copy-muted lg:block">
                  <HistogramLastUpdatedDate updatedAt={histogramUpdatedAt} />
                </span>
              </div>
              <p
                className={cn('text-3xl text-copy-primary font-semibold', {
                  blur: hasBlur,
                })}
                aria-label="average total compensation"
              >
                {totalCompAvg}
              </p>
              <div className="flex justify-between items-center gap-2 md:justify-start">
                <p className="text-xs text-copy-accent">
                  {isHourlyPayPeriod
                    ? 'Avg Total Hourly Comp'
                    : 'Avg Total Comp'}{' '}
                  {Number(salaryCount) > 1 &&
                    `(${formattedSalaryCount} Salaries)`}
                </p>
                <SalaryPayPeriodToggle isAuthenticated={isAuthenticated} />
              </div>
            </div>

            {isLowData && (
              <p
                className={
                  'mt-8 text-center text-copy-muted w-full text-sm lg:text-md'
                }
              >
                Not enough data to show detailed histogram
              </p>
            )}

            {hasHistogramData && (
              <div className="flex-1 relative">
                <HistogramChart
                  data={histogram}
                  isLowData={isLowData}
                  blurAmounts={hasBlur}
                />
              </div>
            )}
          </div>

          {hasHistogramData && (
            <div className="lg:w-1/3">
              {hasBlur ? (
                <div
                  className={cn(
                    'pt-6 h-full flex flex-col items-center text-center justify-center gap-3 text-balance',
                    'border-t border-background-muted',
                    'lg:border-t-0 lg:border-l lg:pt-0 lg:pl-6',
                    'xl:px-8',
                  )}
                >
                  <LockAlertContent
                    salaryRestriction={hasBlur && salaryRestriction}
                    moduleName="histogram"
                    isAuthenticated={isAuthenticated}
                  />
                </div>
              ) : (
                <div className="h-full border-background-muted lg:border-t-0 lg:border-l lg:pt-0 lg:pl-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="flex gap-2">Inside the Averages</p>
                    {Number(salaryCount) > 1 && (
                      <span className="text-xs lg:hidden text-copy-muted">
                        {`${formattedSalaryCount} Salaries`}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-[auto_auto_1fr]">
                    {averageData.map(
                      ({ label, Icon, value, subItems, testId }) => (
                        <Fragment key={label}>
                          <Icon className="w-6 h-6 stroke-copy-muted mt-4" />
                          <span className="px-4 font-medium text-sm mt-4">
                            {label}
                          </span>
                          <span
                            className="text-copy-accent text-sm mt-4"
                            data-testid={testId}
                          >
                            {value}
                          </span>
                          {subItems?.map(({ label, value, testId }) => (
                            <Fragment key={label}>
                              <div className="h-full border-l border-topNav-divider ml-2.5 mb-4" />
                              <span className="font-medium text-sm px-4 mt-3">
                                {label}
                              </span>
                              <span
                                className="text-copy-accent text-sm mt-3"
                                data-testid={testId}
                              >
                                {value}
                              </span>
                            </Fragment>
                          ))}
                        </Fragment>
                      ),
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
